/**
 * Numéro 4 du programme pédagogique ETM — Les autres usagers
 *
 * Thème ETM officiel : Les autres usagers de la route (code thème U).
 * Couvre : piétons, cyclistes, EDPM (trottinettes), poids lourds, transports en commun,
 * usagers vulnérables.
 */

import { ShieldCheck, AlertTriangle, Lightbulb, Quote, BookOpen, Users } from 'lucide-react';

export const autresUsagersEtmIssue = {
  // META
  slug: 'autres-usagers',
  themeCode: 'U',
  issueNumber: 4,
  totalIssues: 9,
  category: 'Le partage de la route',
  shortTitle: 'Les autres usagers',
  title: 'Vivre avec piétons, vélos, camions',
  kicker: 'Numéro 4 du programme pédagogique ETM',
  subtitle:
    "Le partage de la route en milieu urbain et rural. Comment cohabiter intelligemment avec piétons, cyclistes, trottinettes, bus et camions — sans devenir la statistique de demain.",
  heroImage: '/img/magazines/autres-usagers.jpg',
  heroImageAlt: 'Motard franchissant un passage piéton en milieu urbain — illustration du partage de la route',
  readTimeMin: 13,

  // SEO
  seoDescription:
    "Cohabiter avec les autres usagers à moto pour l'ETM 2026 : piétons, cyclistes, EDPM, poids lourds, transports en commun. Règles, angles morts, comportements à risque. Cours + QCM.",
  seoKeywords:
    'partage route moto, piétons cyclistes ETM, angle mort camion, EDPM trottinette, usagers vulnérables',

  // ÉDITO
  edito: {
    title: "La route n'appartient à personne — surtout pas au plus rapide",
    body: "En 2024, <strong>448 piétons</strong> et <strong>226 cyclistes</strong> sont morts sur les routes françaises. Ce sont les <strong>usagers vulnérables</strong>, et toi à moto tu en fais aussi partie. Mais paradoxalement, c'est <strong>toi qui as la responsabilité</strong> face à plus vulnérable que toi — et toi qui dois te méfier face à plus gros. Ce numéro te donne les règles ET les angles morts à connaître pour ne tuer personne… et ne pas l'être non plus.",
    signedBy: 'La rédaction Code 2 Roues',
  },

  // SECTIONS
  sections: [
    // === CHAPITRE 1 — Piétons ===
    {
      type: 'lesson',
      icon: ShieldCheck,
      partLabel: 'Chapitre 1',
      title: 'Les piétons : ta priorité absolue, même hors passage',
      paragraphs: [
        "Article R415-11 du Code de la route : « Tout conducteur est tenu de céder le passage, au besoin en s'arrêtant, au piéton s'engageant régulièrement dans la traversée d'une chaussée OU manifestant clairement l'intention de le faire. »",
        "<strong>Traduction concrète</strong> : tu dois t'arrêter <strong>même si le piéton n'est pas encore sur le passage</strong>, à partir du moment où il manifeste l'intention. Un piéton qui regarde la chaussée et avance d'un pas = intention manifeste.",
        "<strong>Sanction</strong> en cas de non-respect : 135 € + <strong>6 points retirés</strong> (depuis le décret de 2018, l'une des sanctions les plus sévères du code). En cas de blessure : délit, jusqu'à 5 ans de prison.",
        "<strong>Zones à risque maximal</strong> : sorties d'école (8h-9h, 16h-17h), bouches de métro / arrêts de bus (le piéton court pour ne pas le rater), abords des passages piétons en agglomération de nuit (le piéton se croit invisible mais croit te voir).",
        "À noter : depuis 2008, les <strong>aveugles ou malvoyants munis d'une canne blanche</strong> sont prioritaires PARTOUT — pas seulement sur les passages. Idem pour les enfants de moins de 8 ans accompagnés.",
      ],
      keyPoints: [
        'Arrêt obligatoire dès intention manifeste du piéton',
        'Sanction : 135 € + 6 points',
        'Aveugles et enfants : prioritaires partout',
        "Surveillance accrue aux abords des écoles et arrêts de bus",
      ],
    },

    // === SAVIEZ-VOUS ===
    {
      type: 'didyouknow',
      icon: Lightbulb,
      title: 'Le saviez-vous ?',
      body: "Un piéton heurté à <strong>50 km/h</strong> a <strong>20 % de chances de survie</strong>. À <strong>30 km/h</strong>, ces chances montent à <strong>90 %</strong>. C'est précisément pour cela que les municipalités multiplient les <strong>« zones 30 »</strong> et les <strong>« zones de rencontre » (20 km/h max)</strong> où le piéton a TOUJOURS la priorité, même hors passage. À moto, ces zones se reconnaissent au panneau B30 (cercle bleu) ou au marquage spécifique à l'entrée.",
    },

    // === CHAPITRE 2 — Cyclistes ===
    {
      type: 'lesson',
      icon: ShieldCheck,
      partLabel: 'Chapitre 2',
      title: 'Cyclistes & EDPM : les nouvelles règles de cohabitation',
      paragraphs: [
        "Depuis le décret de 2019, le partage avec les cyclistes a été renforcé. Quelques règles à connaître par cœur :",
        "<strong>Distance latérale au dépassement</strong> (R414-4) : <strong>1 mètre en agglomération</strong>, <strong>1,5 mètre hors agglomération</strong>. En dessous : 135 € + 3 points. À moto, c'est plus facile à respecter qu'en voiture — pas d'excuse.",
        "<strong>Sas vélo</strong> : zone marquée entre le passage piéton et la ligne d'effet du feu. <strong>Interdit à tout véhicule motorisé</strong>, y compris ta moto. Sanction : 135 € + 4 points.",
        "<strong>Bandes et pistes cyclables</strong> : interdiction absolue d'y circuler ou stationner. Même pour 30 secondes. La piste cyclable est <strong>physiquement séparée</strong> de la chaussée ; la bande est seulement matérialisée par un marquage.",
        "<strong>EDPM (trottinettes, gyropodes, monoroues)</strong> : depuis 2019, ils sont assimilés aux cyclistes. Vitesse max 25 km/h. Interdits sur trottoir (sauf au pas, sous certaines conditions). Casque obligatoire pour les moins de 12 ans.",
        "<strong>Cédez-le-passage cycliste au feu rouge</strong> (panneau M12) : les cyclistes peuvent désormais franchir un feu rouge dans le sens autorisé par la flèche. <strong>Ça ne te dispense pas d'attendre TON feu vert</strong>.",
      ],
      keyPoints: [
        '1 m en ville, 1,5 m hors agglo pour dépasser un vélo',
        'Sas vélo = strictement interdit aux motos',
        'EDPM : assimilés cyclistes depuis 2019',
        'M12 = priorité cycliste, mais pas moto',
      ],
    },

    // === PIÈGE ===
    {
      type: 'trap',
      icon: AlertTriangle,
      title: 'Le piège à éviter',
      body: "« Le sas vélo est vide, je peux le franchir au feu rouge pour mieux démarrer. » <strong>FAUX et sanctionné.</strong> Le sas vélo est réservé aux cyclistes, point. Même s'il est vide. Même 5 secondes. La sanction est de 135 € + 4 points retirés — c'est l'infraction le plus contrôlée en zone urbaine depuis 2022. À moto, tu attends derrière la première ligne d'effet, comme une voiture.",
    },

    // === CHAPITRE 3 — Poids lourds ===
    {
      type: 'lesson',
      icon: ShieldCheck,
      partLabel: 'Chapitre 3',
      title: 'Les poids lourds : 5 angles morts qui peuvent te tuer',
      paragraphs: [
        "Un camion 38 tonnes a <strong>5 angles morts majeurs</strong>. Tous les motards expérimentés les connaissent. Voici lesquels :",
        "<strong>1. À l'avant immédiat</strong> : zone aveugle d'environ 2 mètres en face du pare-brise (le motard à scooter qui se range juste devant un camion est <em>invisible</em>). <strong>2. Côté droit</strong> : tout le long, du pied du conducteur jusqu'à l'arrière. C'est l'angle mort le plus mortel — il tue le plus de cyclistes et motards qui doublent par la droite. <strong>3. Côté gauche</strong> : zone juste sous la portière. <strong>4. Arrière immédiat</strong> : impossible de te voir dans le rétro central s'il y en a un. <strong>5. Diagonal arrière-gauche</strong> en cas de virage.",
        "<strong>Règle d'or</strong> : si tu ne peux pas voir les yeux du conducteur dans son rétroviseur, il ne te voit pas non plus. Recule ou avance pour sortir de l'angle mort.",
        "<strong>Ne JAMAIS dépasser un camion par la droite</strong>, même en zone CIF autorisée — c'est l'erreur n°1 cause d'accidents mortels.",
        "<strong>Aux intersections</strong> : si un camion clignote à droite et que tu es à sa droite, recule. Sa giration arrière passe physiquement par où tu te trouves.",
      ],
      keyPoints: [
        '5 angles morts à connaître par cœur',
        "Règle d'or : si tu ne vois pas les yeux, tu n'es pas vu",
        'Jamais dépasser un camion par la droite',
        'Camion qui tourne à droite = ne JAMAIS être à sa droite',
      ],
    },

    // === CONSEIL DU MONITEUR ===
    {
      type: 'tip',
      icon: Quote,
      title: 'Le conseil du moniteur ECSR',
      body: "« Quand un élève me demande comment cohabiter avec les camions, je lui réponds simplement : <strong>traite-les comme s'ils étaient aveugles de ton côté</strong>. Tu n'irais pas marcher juste devant un aveugle en lui criant dans les oreilles. Là, c'est pareil. Tu te montres tôt (clignotants, position de visibilité), tu dépasses vite et par la gauche, tu te rabats avec une marge généreuse (au moins 5 longueurs de moto). Et tu ne stationnes JAMAIS dans son angle mort à un feu rouge. »",
    },

    // === CHAPITRE 4 — Transports en commun ===
    {
      type: 'lesson',
      icon: ShieldCheck,
      partLabel: 'Chapitre 4',
      title: 'Bus, tram, taxis : règles spécifiques à connaître',
      paragraphs: [
        "<strong>Couloirs de bus</strong> : à Paris et dans les grandes métropoles, certains couloirs de bus sont <strong>autorisés aux motos</strong> (panneau B27a + mention « + motos »). Vérifie systématiquement : sans le marquage explicite, l'amende est de 135 € + 4 points.",
        "<strong>Bus qui démarre d'un arrêt</strong> : article R415-13 — en agglomération, tu dois <strong>céder le passage</strong> au bus qui signale son intention de démarrer (clignotant gauche). Hors agglomération, c'est l'inverse.",
        "<strong>Tramway</strong> : il a TOUJOURS la priorité, sauf indication contraire. Tu ne dois <strong>jamais t'arrêter sur les rails</strong>, même temporairement. Et attention au franchissement des rails : à moto, traverse à 90° si possible (sinon ta roue avant peut se prendre dans le rail = chute garantie).",
        "<strong>Taxis et VTC</strong> : ils ont le droit d'utiliser les couloirs de bus là où c'est autorisé. Méfie-toi des arrêts brusques (client qui hèle).",
      ],
      keyPoints: [
        "Couloir bus moto : seulement si marquage explicite",
        "Bus qui démarre en ville : priorité au bus",
        "Tramway : toujours prioritaire, traverse les rails à 90°",
        "Taxis : attention aux arrêts brusques",
      ],
    },

    // === TÉMOIGNAGE ===
    {
      type: 'story',
      icon: Users,
      title: "Le témoignage : « J'ai failli tuer un piéton, ça m'a refait moto »",
      body: "« 23 ans, 5 ans de moto, je roulais 60 km/h en ville parce que c'était fluide. Un gamin de 8 ans est sorti d'entre deux voitures stationnées en courant après son ballon. J'ai freiné, l'arrière a chassé, je suis tombé à 2 mètres de lui — il n'a rien eu. La maman a hurlé. J'ai mis 3 jours à dormir. Depuis ce jour-là, en ville je roule à 40 max, et entre des voitures stationnées je passe en seconde. Le piéton imprévu est la règle, pas l'exception. La règle des 50 km/h, c'est un PLAFOND, pas un objectif. » <em>— Hugo, 28 ans</em>",
    },
  ],

  // CHIFFRE
  keyStat: {
    kicker: 'Le chiffre à retenir',
    big: '90 %',
    middle: 'de survie',
    label: "d'un piéton heurté à 30 km/h — contre 20 % à 50 km/h",
    source: 'ONISR — Étude impact vitesse-piéton 2023',
    accent: '#ea580c',
  },

  // GLOSSAIRE
  glossary: [
    { term: 'Usager vulnérable', def: "Catégorie regroupant piétons, cyclistes, EDPM et motards — usagers à risque accru en cas de collision." },
    { term: 'Sas vélo', def: "Zone marquée au sol entre le passage piéton et le feu, réservée aux cyclistes au feu rouge. Interdite aux véhicules motorisés." },
    { term: 'EDPM', def: "Engin de Déplacement Personnel Motorisé : trottinettes électriques, gyropodes, monoroues. Assimilés aux cyclistes depuis 2019." },
    { term: 'Zone 30', def: "Section urbaine où la vitesse est limitée à 30 km/h pour favoriser la cohabitation avec piétons et cyclistes." },
    { term: 'Zone de rencontre', def: "Espace urbain où la vitesse est limitée à 20 km/h et où le piéton a la priorité absolue, même hors passage." },
    { term: 'Panneau M12', def: "Signalisation autorisant le cycliste à franchir un feu rouge dans la direction indiquée par la flèche. Pas valable pour les motos." },
    { term: "Angle mort camion", def: "Zones non visibles depuis le poste de conduite d'un poids lourd. 5 zones principales, dont la droite est la plus mortelle." },
    { term: 'B27a (bus + motos)', def: "Panneau autorisant les motos à utiliser un couloir de bus. Nécessite mention explicite « + motos »." },
    { term: 'Giration arrière', def: "Trajectoire d'un camion lors d'un virage, où l'arrière de la remorque coupe l'intérieur de la courbe." },
    { term: 'R415-11', def: "Article du Code de la route obligeant tout conducteur à céder le passage à un piéton manifestant l'intention de traverser." },
  ],

  // QCM
  quiz: {
    title: 'Teste ta compréhension',
    description:
      "10 questions sur le partage de la route tirées de la banque officielle ETM. Tu dois en réussir <strong>au moins 8 sur 10</strong> pour valider ta maîtrise du thème.",
    themeCode: 'U',
    questionsCount: 10,
    passingScore: 8,
  },

  // NEXT
  nextIssue: {
    slug: 'notions-diverses',
    number: 5,
    title: 'Permis, points, assurance',
    short: 'Notions diverses',
  },
};

export default autresUsagersEtmIssue;
